import type { IconName } from '@customerio/pluma-icons';
import type { ActionType, SearchResult, TemplateType, CampaignType } from '@/services/auth/types';

export const LABEL = 'Search for anything';

export const UNIVERSAL_SEARCH_PREFIXES = {
  segments: 's',
  campaigns: 'c',
  actions: 'a',
  templates: 't',
  navigation: '>',
} as const;

export type UniversalSearchPrefix = (typeof UNIVERSAL_SEARCH_PREFIXES)[keyof typeof UNIVERSAL_SEARCH_PREFIXES];

export function scopedUniversalSearchQuery(prefix: UniversalSearchPrefix, query: string): string {
  return `${prefix}:${query.trim()}`;
}

export type GroupType =
  | Exclude<SearchResult['kind'], 'template'>
  | TemplateType
  | Extract<CampaignType, 'triggered_broadcast'>;

export const ACTION_TYPE_ICONS = {
  conditional_wait_action: 'wait',
  delay_seconds_action: 'time-delay',
  delay_time_window_action: 'time-window',
  grace_period_action: 'time-delay',
  email_action: 'email',
  slack_action: 'slack',
  twilio_action: 'sms',
  line_action: 'sms',
  push_action: 'mobile',
  urban_airship_action: 'mobile',
  in_app_action: 'in-app',
  webhook_action: 'webhook',
  attribute_update_action: 'person',
  static_seg_update_action: 'segment-manual',
  collection_query_action: 'collection',
  create_event_action: 'event',
  batch_update_action: 'person',
  split_randomized_action: 'a-b-test',
  conditional_branch_action: 'branch-two',
  multi_split_branch_action: 'branch-three',
  multi_lang_branch_action: 'localization',
  random_cohort_branch_action: 'random-cohort',
  exit_action: 'exit',
  filter_match_delay_action: 'time-delay',
  randomized_delay_action: 'time-delay',
  trigger: 'trigger-event',
  llm_action: 'sparkle',
  inbox_action: 'notifications',
  whatsapp_action: 'sms',
} as const satisfies Record<ActionType, IconName>;

export const TEMPLATE_TYPE_ICONS = {
  email: 'email',
  webhook: 'webhook',
  twilio: 'sms',
  urban_airship: 'mobile',
  slack: 'slack',
  push: 'mobile',
  create_event: 'trigger-event',
  attribute_update: 'person',
  batch_update_not_configured: 'person',
  batch_create_event: 'person',
  batch_attribute_update: 'person',
  in_app: 'in-app',
  in_app_broadcast: 'in-app',
  llm_action_prompt: 'sparkle',
  llm_action_result_schema: 'sparkle',
  line: 'sms',
  inbox: 'notifications',
  whatsapp: 'sms',
} as const satisfies Record<TemplateType, IconName>;

export const TEMPLATE_TYPE_LABELS = {
  email: 'Email',
  webhook: 'Webhook',
  twilio: 'SMS',
  urban_airship: 'Airship',
  slack: 'Slack Message',
  push: 'Push Notification',
  line: 'LINE Message',
  inbox: 'Inbox Message',
  whatsapp: 'WhatsApp',
  in_app: 'In-App Message',
  in_app_broadcast: 'In-App Message',
  create_event: 'Send Event',
  attribute_update: 'Create or Update Person',
  batch_update_not_configured: 'Batch Update',
  batch_create_event: 'Batch Update',
  batch_attribute_update: 'Batch Update',
  llm_action_prompt: 'Run LLM',
  llm_action_result_schema: 'Run LLM',
} as const satisfies Record<TemplateType, string>;

export const SEARCH_RESULT_HEADINGS = {
  customer: 'People',
  campaign: 'Campaigns',
  triggered_broadcast: 'API Triggered Broadcasts',
  segment: 'Segments',
  action: 'Actions',
  navigation: 'Navigation',
  newsletter: 'Newsletters',
  // template types as we are grouping templates by type
  ...TEMPLATE_TYPE_LABELS,
} as const satisfies Record<GroupType, string>;

export const CAMPAIGN_TYPE_ICONS = {
  seg_attr: 'segment',
  transactional: 'trigger-event',
  behavioral: 'segment',
  triggered_broadcast: 'api-triggered-broadcast',
  date: 'trigger-date',
  form: 'form',
  webhook: 'webhook',
  object: 'object',
  relationship: 'connections',
  none: 'campaigns',
} as const satisfies Record<CampaignType, IconName>;
